import clsx from "clsx";
import React from "react";
import { createPluggable } from "pluggable-react";
import styles from "./Alert.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

/**
 * Alert has its own variant definitions (independent of Chip) but uses the same variant
 * names so the two read as one system. Values come from the shared *-light / *-light-foreground
 * theme tokens in tailwind.css.
 */
export const alertVariants = {
  success: {
    color: "var(--success-light-foreground)",
    backgroundColor: "var(--success-light)",
  },
  destructive: {
    color: "var(--destructive-light-foreground)",
    backgroundColor: "var(--destructive-light)",
  },
  warning: {
    // --warning-light-foreground isn't defined in the theme; fall back to --dark
    color: "var(--warning-light-foreground, var(--dark))",
    backgroundColor: "var(--warning-light)",
  },
  info: {
    // Matches the outlined info box in CreateMappingDialog.tsx (Tailwind blue-50/200/950) —
    // deliberately not --info-light, which is a more saturated periwinkle used by Chip.
    color: "var(--color-blue-950)",
    backgroundColor: "var(--color-blue-50)",
    borderColor: "var(--color-blue-200)",
  },
  subtle: {
    color: "var(--subtle-light-foreground)",
    backgroundColor: "var(--subtle-light)",
  },
  secondary: {
    color: "var(--secondary-light-foreground)",
    backgroundColor: "var(--secondary-light)",
  },
} as const;

export type AlertVariantKey = keyof typeof alertVariants;

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  variant?: AlertVariantKey;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

const DefaultAlert = withRefProp((props: AlertProps) => {
  const { className, color, backgroundColor, borderColor, variant = "info", icon, title, description, style, children, ref, ...otherProps } = props;
  const variantStyles: Partial<{ color: string; backgroundColor: string; borderColor: string }> | undefined = variant ? alertVariants[variant] : undefined;
  const effectiveColor = color ?? variantStyles?.color;
  const effectiveBackgroundColor = backgroundColor ?? variantStyles?.backgroundColor;
  const effectiveBorderColor = borderColor ?? variantStyles?.borderColor;
  const content = children ?? (
    <AlertContent>
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      {description ? <AlertDescription>{description}</AlertDescription> : null}
    </AlertContent>
  );
  return (
    <div
      ref={ref}
      role="alert"
      className={clsx(styles.alert, className)}
      data-deci-alert="true"
      data-variant={variant}
      style={{ ...style, color: effectiveColor, backgroundColor: effectiveBackgroundColor, borderColor: effectiveBorderColor }}
      {...otherProps}
    >
      {icon ? <AlertIcon>{icon}</AlertIcon> : null}
      {content}
    </div>
  );
});

const Alert = createPluggable<AlertProps>("deci.alert", DefaultAlert);

export interface AlertIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  ref?: React.Ref<HTMLSpanElement>;
}

const AlertIcon = withRefProp((props: AlertIconProps) => {
  const { className, ref, ...otherProps } = props;
  return <span ref={ref} className={clsx(styles.alertIcon, className)} {...otherProps} />;
});
AlertIcon.displayName = "Alert.Icon";

export interface AlertContentProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
}

const AlertContent = withRefProp((props: AlertContentProps) => {
  const { className, ref, ...otherProps } = props;
  return <div ref={ref} className={clsx(styles.alertContent, className)} {...otherProps} />;
});
AlertContent.displayName = "Alert.Content";

export interface AlertTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
}

const AlertTitle = withRefProp((props: AlertTitleProps) => {
  const { className, ref, ...otherProps } = props;
  return <div ref={ref} className={clsx(styles.alertTitle, className)} {...otherProps} />;
});
AlertTitle.displayName = "Alert.Title";

export interface AlertDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>;
}

const AlertDescription = withRefProp((props: AlertDescriptionProps) => {
  const { className, ref, ...otherProps } = props;
  return <div ref={ref} className={clsx(styles.alertDescription, className)} {...otherProps} />;
});
AlertDescription.displayName = "Alert.Description";

export default Object.assign(Alert, {
  Icon: AlertIcon,
  Content: AlertContent,
  Title: AlertTitle,
  Description: AlertDescription,
});
